import React, { useState, useEffect, useRef } from 'react';
import AppMockup from './AppMockup';
import { trackGetStartedClick } from '../utils/analytics';
import { isPrerendering } from '../utils/prerendering';
import ScheduleDemoModal from './ScheduleDemoModal';
import './Hero.css';

// The dynamic line reads like the agent narrating what it does, typed live.
const TYPED_PHRASES = [
  'uses your app like a real user',
  'tests through the UI',
  'fixes the bugs it finds',
];

const Hero = () => {
  const [showScheduleDemoModal, setShowScheduleDemoModal] = useState(false);
  const heroRef = useRef(null);
  const typingIntervalRef = useRef(null);

  // Redirect to testmindfrontend (app.qate.ai) for signup
  const saasUrl = process.env.REACT_APP_SAAS_URL || 'https://app.qate.ai';

  const handleCTAClick = (e) => {
    e.preventDefault();
    trackGetStartedClick('hero');
    window.gtag_report_conversion(`${saasUrl}/get-started`);
  };

  const handleScheduleDemoClick = (e) => {
    e.preventDefault();
    setShowScheduleDemoModal(true);
  };

  // Typewriter effect: cycles through TYPED_PHRASES in the headline's dynamic line.
  useEffect(() => {
    // Skip during pre-render so captured HTML matches React's initial render.
    if (isPrerendering) return;

    const textElement = heroRef.current?.querySelector('.typing-text');
    if (!textElement) return;

    let currentTextIndex = 0;
    let currentCharIndex = 0;
    let isDeleting = false;
    let isWaiting = false;

    textElement.textContent = '';
    textElement.classList.add('active');

    if (typingIntervalRef.current) {
      clearTimeout(typingIntervalRef.current);
    }

    const typeWriter = () => {
      if (!textElement) return;

      if (isWaiting) {
        typingIntervalRef.current = setTimeout(() => {
          isWaiting = false;
          isDeleting = true;
          typeWriter();
        }, 4000);
        return;
      }

      const currentText = TYPED_PHRASES[currentTextIndex];

      if (!isDeleting) {
        if (currentCharIndex < currentText.length) {
          textElement.textContent = currentText.substring(0, currentCharIndex + 1);
          currentCharIndex++;
          typingIntervalRef.current = setTimeout(typeWriter, 80);
        } else {
          isWaiting = true;
          typeWriter();
        }
      } else {
        if (currentCharIndex > 0) {
          textElement.textContent = currentText.substring(0, currentCharIndex - 1);
          currentCharIndex--;
          typingIntervalRef.current = setTimeout(typeWriter, 45);
        } else {
          isDeleting = false;
          currentTextIndex = (currentTextIndex + 1) % TYPED_PHRASES.length;
          typingIntervalRef.current = setTimeout(typeWriter, 500);
        }
      }
    };

    typingIntervalRef.current = setTimeout(typeWriter, 900);

    return () => {
      if (typingIntervalRef.current) {
        clearTimeout(typingIntervalRef.current);
      }
    };
  }, []);

  return (
    <section className="hero" ref={heroRef}>
      {/* Ambient backdrop: one soft brand wash + a faint technical grid (a nod to
          the coordinate space the agent navigates). Everything else stays quiet. */}
      <div className="hero-bg" aria-hidden="true">
        <div className="hero-bg-wash"></div>
        <div className="hero-bg-grid"></div>
      </div>

      <div className="hero-container">
        <div className="hero-content">
          <span className="hero-eyebrow">
            <span className="hero-eyebrow-dot"></span>
            Autonomous QA agent
          </span>

          <h1 className="hero-headline">
            <span className="hero-headline-lead">GenAI Quality Assurance</span>
            <span className="hero-headline-dynamic">
              that <span className="typing-text" data-typing="true" suppressHydrationWarning></span>
            </span>
          </h1>

          <p className="hero-description">
            <strong>AI that clicks, types, and navigates</strong> just like a real user —
            exploring your app, writing comprehensive tests, and shipping
            {' '}<strong className="hero-accent-pink">automated bugfixes</strong> when something breaks.
          </p>

          <div className="hero-action-card">
            <ul className="hero-checks">
              <li className="hero-check">
                <span className="hero-check-icon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <span className="hero-check-label">AI clicks, types, and navigates your app</span>
              </li>
              <li className="hero-check">
                <span className="hero-check-icon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <span className="hero-check-label">Self-healing tests + <span className="hero-accent-pink">automated bug fixing</span></span>
              </li>
              <li className="hero-check">
                <span className="hero-check-icon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <span className="hero-check-label">Web + <span className="hero-accent-blue">Windows desktop</span> + API testing</span>
              </li>
            </ul>

            <div className="hero-cta-section">
              <div className="hero-buttons">
                <button className="hero-cta-button hero-cta-primary" onClick={handleCTAClick}>
                  <span className="hero-btn-content">
                    <span className="hero-btn-label">Get Started Free</span>
                    <span className="hero-btn-sub">No credit card · Setup in 60s</span>
                  </span>
                  <span className="hero-btn-arrow">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </span>
                </button>
                <button className="hero-cta-button hero-cta-secondary" onClick={handleScheduleDemoClick}>
                  <span className="hero-btn-icon-secondary">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  </span>
                  <span className="hero-btn-content">
                    <span className="hero-btn-label-secondary">Schedule Demo</span>
                    <span className="hero-btn-sub-secondary">15 min · Live walkthrough</span>
                  </span>
                </button>
              </div>
              <p className="hero-cta-note">Free forever · Create with us, run anywhere</p>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          {/* Hand-drawn annotation pointing at the live agent demo (desktop only). */}
          <div className="hero-visual-note" aria-hidden="true">
            <span className="hero-visual-note-text">watch the agent work — live</span>
            <svg className="hero-visual-note-arrow" viewBox="0 0 80 60" fill="none">
              <path d="M6 6 Q40 10 52 34 Q57 44 56 52" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
              <path d="M48 46 L57 54 L62 44" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </div>
          <div className="visual-container">
            <div className="visual-glow"></div>
            <AppMockup />
          </div>
        </div>
      </div>

      {/* Trust marquee strip */}
      <div className="hero-trust-strip">
        <div className="trust-marquee">
          <div className="trust-track">
            {[...Array(4)].flatMap(() => ['Web Testing', 'Desktop Apps', 'API Testing', 'Self-Healing', 'Auto Bug Fix', 'CI/CD Ready', 'Cross-Browser', 'Visual Regression']).map((item, i) => (
              <span className="trust-item" key={i}>
                <span className="trust-diamond">&#9670;</span>
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {showScheduleDemoModal && <ScheduleDemoModal onClose={() => setShowScheduleDemoModal(false)} />}
    </section>
  );
};

export default Hero;
